<template>
	<div class="flow-dag-chart-wrapper">
		<div v-if="showControls" class="flow-dag-chart-controls">
			<div v-if="$slots['controls']" class="flow-dag-chart-custom-controls">
				<slot name="controls" />
			</div>
			<Icon
				v-tooltip="{
					content: '<span class=\'fc-dark paragraph-2\'>Reset</span>',
					placement: 'bottom',
					classes: ['black']
				}"
				name="reload"
				type="outlined"
				:disabled="!enableReset"
				size="small"
				@click="reset()"
			/>
			<Icon name="vert-line" type="outlined" :disabled="true" size="small" class="margin-lt-12" />
			<Icon
				v-tooltip="{
					content: '<span class=\'fc-dark paragraph-2\'>Zoom in</span>',
					placement: 'bottom',
					classes: ['black']
				}"
				name="i-plus"
				type="filled"
				size="small"
				class="margin-lt-12"
				@click="zoomIn()"
			/>
			<Icon
				v-tooltip="{
					content: '<span class=\'fc-dark paragraph-2\'>Zoom out</span>',
					placement: 'bottom',
					classes: ['black']
				}"
				name="minus"
				type="outlined"
				size="small"
				class="margin-lt-24"
				@click="zoomOut()"
			/>
		</div>
		<div :id="id" class="flow-dag-chart" />
	</div>
</template>
<script>
import * as d3 from "d3";
import dagreD3 from "@cldcvr/dagre-d3";
import DagNode from "./../DagChart/DagNode.vue";
import Vue, { defineComponent } from "vue";
import Icon from "./../Icon/Icon.vue";
import { VTooltip } from "v-tooltip";

export default defineComponent({
	name: "DagChart",
	components: {
		// eslint-disable-next-line vue/no-unused-components
		DagNode,
		Icon
	},

	directives: {
		tooltip: VTooltip
	},
	props: {
		/**
		 * [	{
						name: String,
						nodeNumber: String,
						type: String,
						statusText: String,
						status: String
					}]
		 */
		nodes: {
			type: Array,
			default: () => []
		},
		/**
		 * [{ source: String, target: String }]
		 */
		edges: {
			type: Array,
			default: () => []
		},
		/**
		 * Height of svg
		 */
		height: {
			type: Number,
			default: 900
		},
		/**
		 * Direction of nodes
		 */
		direction: {
			type: String,
			default: "horizontal",
			validator(value) {
				return ["horizontal", "vertical"].includes(value);
			}
		},
		/**
		 * true | false
		 */
		compact: {
			type: Boolean,
			default: false
		},
		/**
		 *  'UL' | 'UR' | 'DL' | 'DR'
		 */
		align: {
			type: String,
			default: null,
			validator(value) {
				return ["UL", "UR", "DL", "DR"].includes(value);
			}
		},
		/**
		 * shows zoom controls and controls slot
		 */
		showControls: {
			type: Boolean,
			default: true
		},
		nodeComponent: {
			type: [Object, Function],
			default: () => {
				return DagNode;
			}
		},
		/**
		 * node config such as {width:number,height:number}
		 */
		nodeConfig: {
			type: Object,
			// eslint-disable-next-line @typescript-eslint/no-empty-function
			default: () => {}
		},
		/**
		 * layout related config
		 */
		layoutConfig: {
			type: Object,
			// eslint-disable-next-line @typescript-eslint/no-empty-function
			default: () => {}
		}
	},
	data() {
		return {
			id: "DagChart",
			graph: null,
			container: null,
			render: null,
			zoom: null,
			svg: null,
			enableReset: false
		};
	},
	computed: {
		vueNodeComponent() {
			if (this.nodeComponent) {
				return Vue.extend(this.nodeComponent);
			} else {
				return Vue.extend(DagNode);
			}
		}
	},
	watch: {
		nodes: {
			handler() {
				/**
				 * resetting zoom if it is applied
				 */
				//	this.svg.call(this.zoom.transform, d3.zoomIdentity);
				//	console.log("Update Nodes");
				this.reset();
				this.createNodes(this.graph);
				this.renderGraph();
			},
			deep: true
		},
		edges: {
			handler() {
				/**
				 * resetting zoom if it is applied
				 */
				//	this.svg.call(this.zoom.transform, d3.zoomIdentity);
				this.reset();
				//	console.log("Update Edges");
				this.createEdges(this.graph);
				this.renderGraph();
			},
			deep: true
		},
		direction: {
			handler() {
				// console.log("Direction changed");
				document.querySelector(`#${this.id}`).innerHTML = "";
				this.plot();
			}
		},
		align: {
			handler() {
				// console.log("Direction changed");
				document.querySelector(`#${this.id}`).innerHTML = "";
				this.plot();
			}
		},
		compact: {
			handler() {
				document.querySelector(`#${this.id}`).innerHTML = "";
				this.plot();
			}
		}
	},
	beforeMount() {
		/**
		 * Generate Id and assign
		 */
		this.id = this.generateChartId();
	},
	mounted() {
		this.plot();
	},
	methods: {
		reset() {
			// console.log("resetting");
			if (this.container) {
				const t = d3.zoomTransform(this.container.node());
				t.k = 1;
				t.x = 0;
				t.y = 0;
				this.zoomContainer(t);
				this.enableReset = false;
			}
			// this.zoom.scaleTo(this.container, 1);
			// this.zoom.translateTo(this.container, 0, 0);

			// this.container.transition().duration(100).call(this.zoom.scaleTo, 1);
			// this.container.transition().duration(100).call(this.zoom.translateTo, 0, 0, 1);
		},
		zoomIn() {
			// console.log("zooming in");
			if (this.container) {
				const t = d3.zoomTransform(this.container.node());
				t.k += 0.1;
				if (t.k > 4) {
					t.k = 4;
				}
				this.zoomContainer(t);
			}
			// console.log(t);
			// this.container.transition().duration(100).call(this.zoom.scaleBy, 1.1);
			// this.container
			// 	.transition()
			// 	.duration(100)
			// 	.call(this.zoom.translate, this.containerPosition.x, this.containerPosition.y);
			// this.zoom.scaleTo(this.container, this.containerPosition.k + 0.1);
			// this.zoom.translateTo(this.container, this.containerPosition.x, this.containerPosition.y);
		},
		zoomOut() {
			// console.log("zooming out");
			if (this.container) {
				const t = d3.zoomTransform(this.container.node());
				t.k -= 0.1;
				if (t.k < 0.3) {
					t.k = 0.3;
				}
				this.zoomContainer(t);
			}
			// this.zoom.scaleTo(this.container, this.containerPosition.k - 0.1);
			// this.zoom.translateTo(this.container, this.containerPosition.x, this.containerPosition.y);
			// this.container.transition().duration(100).call(this.zoom.scaleBy, 0.9);
			// this.container
			// 	.transition()
			// 	.duration(100)
			// 	.call(this.zoom.translateTo, this.containerPosition.x, this.containerPosition.y);
		},
		plot() {
			Promise.resolve(
				new dagreD3.graphlib.Graph().setGraph({
					nodesep: 50,
					ranksep: 100,
					rankdir: this.direction === "horizontal" ? "LR" : "TB",
					align: this.align,
					marginx: 12,
					marginy: 20,
					...this.layoutConfig
				})
			)
				.then(this.createNodesAndEdges)
				.then(this.initRender);
		},
		createNodesAndEdges(graph) {
			if (graph) {
				/**
				 * Creat nodes using node component
				 */
				this.createNodes(graph);

				/**
				 * Create Edges to connect nodes
				 */
				this.createEdges(graph);
			}

			return graph;
		},
		createNodes(graph) {
			this.nodes.forEach(node => {
				node.compact = this.compact;
				const nodeInstance = new this.vueNodeComponent({ data: node });
				const htmlNode = nodeInstance.$mount().$el;
				if (graph) {
					graph.setNode(node.name, {
						label: htmlNode,
						labelType: "html",
						// rx: 5,
						// ry: 5,
						marginy: 50,
						width: 240,
						height: this.compact ? 30 : 80,
						shape: "rect",
						...this.nodeConfig
					});
				}
			});
		},
		createEdges(graph) {
			this.edges.forEach(function (edge) {
				if (graph) {
					graph.setEdge(edge.source, edge.target, {
						arrowheadClass: "arrowhead",
						arrowhead: "undirected",
						class: "normal",
						lineInterpolate: "bundle",
						curve: d3.curveStepBefore
					});
				}
			});
		},
		zoomContainer(transform) {
			// console.log(transform);
			this.enableReset = true;
			// this.container.transition().duration(100).attr("transform", transform);
			this.container.attr("transform", transform);
			if (this.isSafari()) {
				document
					.querySelectorAll(".flow-dag-chart .stage-widget,.flow-dag-chart .dag-node")
					.forEach(elem => {
						const scale = transform.k;
						elem.style.transform = `scale(${scale}) `;
						elem.style["transform-origin"] = "0% 0%";
					});
			}
		},
		initRender(graph) {
			/**
			 * create render for graph
			 */
			this.render = new dagreD3.render();
			const thisComponent = this;

			/**
			 * Create svg and append in template
			 */
			thisComponent.svg = d3.select(`#${thisComponent.id}`).append("svg");
			thisComponent.container = thisComponent.svg.append("g");

			/**
			 * Binding zoom feature
			 */
			thisComponent.zoom = d3
				.zoom()
				.scaleExtent([0.3, 4])
				.on("zoom", () => {
					thisComponent.zoomContainer(d3.event.transform);
				});

			thisComponent.svg.attr("height", thisComponent.height - 16).call(thisComponent.zoom);
			/**
			 * Holding refrence of graph
			 */
			this.graph = graph;

			this.renderGraph();
		},
		renderGraph() {
			if (this.render) {
				/**
				 * Calling render function
				 */
				this.render(this.container, this.graph);

				/**
				 * This for Safari only to fix positions
				 */
				this.fixInSafari();

				d3.selectAll("svg g.node").on("click", d => {
					this.$emit("click", d);
				});
			}
		},
		/**
		 * Generate random ID for each Instance
		 */
		generateChartId() {
			let id = "";
			const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz";
			const charsLength = chars.length;
			for (let i = 0; i < 10; i++) {
				id += chars.charAt(Math.floor(Math.random() * charsLength));
			}
			return id;
		},
		/**
		 * To check if Browser is Safari
		 */
		isSafari() {
			return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
		},
		/**
		 * Apply position fixed for nodes
		 */
		fixInSafari() {
			if (this.isSafari()) {
				setTimeout(() => {
					document
						.querySelectorAll(".flow-dag-chart .stage-widget,.flow-dag-chart .dag-node")
						.forEach(elem => {
							elem.style.position = "fixed";
						});
				}, 100);
			}
		}
	}
});
</script>
<style lang="scss">
div.flow-dag-chart-wrapper {
	width: 100%;
	height: 100%;
	div.flow-dag-chart-controls {
		display: flex;
		align-items: center;
		justify-content: flex-end;

		div.flow-dag-chart-custom-controls {
			display: flex;
			align-items: center;
			margin-right: auto;
			> * + * {
				margin-left: 12px;
			}
		}
	}
	> * + * {
		margin-top: 12px;
	}
}
div.flow-dag-chart {
	background-color: var(--dashboard-background);
	overflow: hidden;
	width: 100%;
	height: calc(100% - 16px);
	> svg {
		width: 100%;
		background-color: var(--dashboard-background);
		cursor: grab;
		&:active {
			cursor: grabbing;
		}
		g.edgePaths {
			> g.edgePath {
				&.normal {
					> path {
						stroke: var(--dashboard-border-light);
						stroke-width: 1px;
					}
				}

				.arrowhead {
					stroke: var(--dashboard-border-light);
					fill: var(--dashboard-border-light);
					stroke-width: 2px;
				}
			}
		}

		g.nodes {
			> g.node {
				> rect.label-container,
				> circle.label-container {
					fill: transparent;
				}
				> g.label {
					tspan {
						fill: var(--dashboard-font);
					}
				}
			}
		}
		.stage-widget {
			transform: scale(0.8);
			// margin-left: 4px;
			// margin-top: 10px;
			box-sizing: content-box;

			&[selected] {
				box-shadow: 0 0 5px 2px var(--dashboard-primary);
				border: 2px solid var(--dashboard-primary);
			}
		}

		g.output {
			transform: translate(10px, 10px);
		}
	}
}
/**
* For safari only
*/
@media not all and (min-resolution: 0.001dpcm) {
	@supports (-webkit-appearance: none) {
		.flow-dag-chart {
			> svg {
				div.stage-widget {
					margin-left: -10px;
					transform: scale(0.9);
				}
			}

			foreignObject {
				> div {
					transform: scale(0.8);
					position: fixed;
					transform-origin: 0% 0%;
				}
			}
		}
	}
}

foreignObject {
	overflow: visible;
}

/**
* For chrome only
*/
@media screen and (-webkit-min-device-pixel-ratio: 0) and (min-resolution: 0.001dpcm) {
	.flow-dag-chart {
		foreignObject {
			> div {
				overflow: overlay;
			}
		}
	}
}
</style>
